import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Image, Music, Film, Sparkles, Search, Volume2, Play, Pause, Upload, Download, X, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/utils/cn';
import {
  getCategorizedLibraryAssets,
  uploadAssetFile,
  type AssetScope,
  type LibraryCategory,
  type PersonalCategory,
  type CategorizedAssetItem,
} from '@/api/modules/assets';

const SCOPE_TABS: Array<{ key: AssetScope; label: string }> = [
  { key: 'public', label: '公共资产库' },
  { key: 'personal', label: '我的资产库' },
];

const PUBLIC_CATEGORIES: Array<{ key: LibraryCategory; label: string; icon: typeof Image }> = [
  { key: 'image', label: '图片', icon: Image },
  { key: 'particle', label: '粒子', icon: Sparkles },
  { key: 'animation', label: '动图', icon: Film },
  { key: 'soundEffect', label: '音效', icon: Volume2 },
  { key: 'bgm', label: '背景音乐', icon: Music },
];

const PERSONAL_CATEGORIES: Array<{ key: LibraryCategory; label: string; icon: typeof Image }> = [
  { key: 'image', label: '图片', icon: Image },
  { key: 'animation', label: '动图', icon: Film },
  { key: 'audio', label: '音频', icon: Music },
];

function formatSize(bytes?: number): string {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

/**
 * 生成页码序列（含省略号）。当前页居中，首尾页常驻，中间用 '...' 折叠。
 * 例：totalPages=10, current=5 → [1,'...',4,5,6,'...',10]
 */
function buildPageList(current: number, totalPages: number): Array<number | '...'> {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
  const pages: Array<number | '...'> = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(totalPages - 1, current + 1);
  if (start > 2) pages.push('...');
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < totalPages - 1) pages.push('...');
  pages.push(totalPages);
  return pages;
}

/** 素材来源徽章配置：区分 AI 生成与个人上传，公共库/未知来源不展示。 */
function sourceBadge(source?: CategorizedAssetItem['source']): { label: string; className: string } | null {
  if (source === 'user_upload' || source === 'user_replaced') {
    return { label: '我的上传', className: 'bg-neon-cyan/85 text-black' };
  }
  if (source === 'ai_generated') {
    return { label: 'AI 生成', className: 'bg-neon-purple/85 text-white' };
  }
  return null;
}

/** 从文件 MIME/扩展名推断个人资产库类目：gif→动图，audio→音频，其余图片。 */
function inferPersonalCategory(file: File): PersonalCategory {
  const mime = file.type.toLowerCase();
  const name = file.name.toLowerCase();
  if (mime.startsWith('audio/') || /\.(mp3|wav|ogg|m4a|aac)$/.test(name)) return 'audio';
  if (mime === 'image/gif' || name.endsWith('.gif')) return 'animation';
  return 'image';
}

/**
 * 下载素材到本地。CDN 跨域资源用 blob 拉取后触发下载（原生 download 属性对跨域链接无效）；
 * 拉取失败降级为新窗口打开，避免完全无响应。
 */
async function downloadToLocal(url: string, fileName: string): Promise<void> {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
  } catch {
    window.open(url, '_blank', 'noopener');
  }
}

export function MaterialsPage(): JSX.Element {
  const [scope, setScope] = useState<AssetScope>('public');
  const [category, setCategory] = useState<LibraryCategory>('image');
  const [assets, setAssets] = useState<CategorizedAssetItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const reqIdRef = useRef(0);
  const [search, setSearch] = useState('');
  const [subcategories, setSubcategories] = useState<string[]>([]);
  const [subcategory, setSubcategory] = useState('');
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [previewItem, setPreviewItem] = useState<CategorizedAssetItem | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const PAGE_SIZE = 60;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const categories = useMemo(
    () => (scope === 'public' ? PUBLIC_CATEGORIES : PERSONAL_CATEGORIES),
    [scope],
  );

  // scope 切换时重置类目到第一个
  useEffect(() => {
    setCategory(categories[0].key);
    setSubcategory('');
  }, [scope, categories]);

  // 筛选条件变化时回到第 1 页（页码变化本身由下方 effect 触发拉取）
  useEffect(() => {
    setPage(0);
  }, [scope, category, subcategory, search]);

  const fetchAssets = useCallback(async () => {
    setLoading(true);
    const reqId = ++reqIdRef.current;
    try {
      const res = await getCategorizedLibraryAssets({
        scope,
        category,
        subcategory: (scope === 'public' && subcategory) ? subcategory : undefined,
        search: search || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      // 竞态保护：仅接受最新一次请求的结果
      if (reqId !== reqIdRef.current) return;
      setAssets(res.items);
      setTotal(res.total);
      if (!page) setSubcategories(res.subcategories ?? []);
    } catch {
      if (reqId !== reqIdRef.current) return;
      setAssets([]);
      setTotal(0);
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  }, [scope, category, subcategory, search, page, PAGE_SIZE]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  // 换页后滚动区域回到顶部
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [page]);

  // 二级类目排序：按钮、背景图、角色优先，其余按字母序
  const sortedSubcategories = useMemo(() => {
    const priority: Record<string, number> = { '按钮': 1, '背景图': 2, '角色': 3 };
    return [...subcategories].sort((a, b) => {
      const pa = priority[a] ?? 99;
      const pb = priority[b] ?? 99;
      if (pa !== pb) return pa - pb;
      return a.localeCompare(b, 'zh-CN');
    });
  }, [subcategories]);

  const isAudio = category === 'audio' || category === 'soundEffect' || category === 'bgm';

  // 上传素材到「我的资产库」：按文件类型自动落库到 图片/动图/音频 类目
  const handleUpload = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    let success = 0;
    const touchedCategories = new Set<PersonalCategory>();
    try {
      for (const file of Array.from(files)) {
        const targetCategory = inferPersonalCategory(file);
        try {
          // slotName 在个人库中作为子类目分组名，统一用「我的上传」聚合，避免每个文件各成一组
          await uploadAssetFile(file, { category: targetCategory, slotName: '我的上传' });
          touchedCategories.add(targetCategory);
          success += 1;
        } catch {
          toast.error(`上传失败: ${file.name}`);
        }
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
    if (success > 0) {
      toast.success(`已上传 ${success} 个素材到我的资产库`);
      // 切到个人库对应类目，让用户立即看到上传结果。
      // 注意：scope 变化的副作用会把 category 重置为首个（image），
      // 故用微任务在其后再设置目标类目，避免被覆盖。
      const target = [...touchedCategories][0];
      if (scope !== 'personal') {
        setScope('personal');
        setTimeout(() => setCategory(target), 0);
      } else if (target !== category) {
        setCategory(target);
      } else {
        fetchAssets();
      }
    }
  }, [scope, category, fetchAssets]);

  return (
    <div className="flex flex-1 flex-col">
      <div className="page-header flex items-center justify-between">
        <h2 className="page-title" style={{ animation: 'none' }}>
          <Image size={18} className="shrink-0 text-neon-cyan" />
          资产库
        </h2>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,audio/*"
          className="hidden"
          onChange={e => handleUpload(e.target.files)}
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="glow-btn flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs disabled:opacity-60"
        >
          {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
          {uploading ? '上传中...' : '上传素材'}
        </button>
      </div>

      {/* 公共/个人资产库 Tab */}
      <div className="mb-3 flex items-center gap-1 border-b border-border pb-2">
        {SCOPE_TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setScope(t.key)}
            className={cn(
              'relative px-3 py-1.5 text-sm font-medium transition-colors',
              scope === t.key ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {t.label}
            {scope === t.key && <span className="absolute bottom-[-9px] left-0 h-0.5 w-full bg-primary" />}
          </button>
        ))}
      </div>

      {/* 类目 Tab + 搜索 */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {categories.map(t => (
          <button
            key={t.key}
            onClick={() => setCategory(t.key)}
            className={cn(
              'flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors',
              category === t.key
                ? 'bg-primary/15 text-primary'
                : 'text-muted-foreground hover:bg-muted/30 hover:text-foreground',
            )}
          >
            <t.icon size={13} />
            {t.label}
          </button>
        ))}

        <div className="ml-auto flex items-center gap-2">
          <div className="relative">
            <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              className="input-neon w-48 py-1.5 pl-7 pr-3 text-xs"
              placeholder="搜索..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* 二级类目：仅公共资产库展示 */}
      {scope === 'public' && subcategories.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-1">
          <button
            onClick={() => setSubcategory('')}
            className={cn(
              'rounded px-2 py-0.5 text-[11px] transition-colors',
              !subcategory ? 'bg-neon-cyan/15 text-neon-cyan font-medium' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            全部
          </button>
          {sortedSubcategories.map(sub => (
            <button
              key={sub}
              onClick={() => setSubcategory(sub)}
              className={cn(
                'rounded px-2 py-0.5 text-[11px] transition-colors',
                subcategory === sub ? 'bg-neon-cyan/15 text-neon-cyan font-medium' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {sub}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex flex-1 items-center justify-center py-20">
          <div className="flex flex-col items-center gap-2 text-muted-foreground">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-neon-cyan border-t-transparent" />
            <span className="text-xs">加载中...</span>
          </div>
        </div>
      ) : assets.length === 0 ? (
        <div className="flex flex-1 items-center justify-center py-20 text-xs text-muted-foreground">
          暂无素材
        </div>
      ) : (
        <div ref={scrollRef} className="flex flex-1 flex-col overflow-auto pr-1">
          <div className="grid grid-cols-4 gap-3 md:grid-cols-5 lg:grid-cols-6">
            {assets.map(item => (
                  <div
                    key={item.id}
                    className="glass-card group cursor-pointer overflow-hidden"
                    onClick={() => setPreviewItem(item)}
                  >
                    <div className="relative aspect-square bg-muted/50">
                      {/* 来源标识（左上角，仅个人库展示） */}
                      {(() => {
                        const badge = sourceBadge(item.source);
                        return badge ? (
                          <span className={cn('absolute left-1.5 top-1.5 z-10 rounded px-1.5 py-0.5 text-[10px] font-medium', badge.className)}>
                            {badge.label}
                          </span>
                        ) : null;
                      })()}
                      {/* 下载按钮（悬浮显示） */}
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          downloadToLocal(item.url, item.name);
                        }}
                        title="下载到本地"
                        className="absolute right-1.5 top-1.5 z-10 rounded-md bg-black/50 p-1 text-white opacity-0 transition-opacity hover:bg-primary/80 group-hover:opacity-100"
                      >
                        <Download size={13} />
                      </button>
                      {isAudio ? (
                        <div className="flex h-full w-full flex-col items-center justify-center gap-2 p-2">
                          <Music size={24} className="text-muted-foreground/50" />
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              setPlayingId(playingId === item.id ? null : item.id);
                            }}
                            className="rounded-full bg-primary/10 p-1.5 text-primary hover:bg-primary/20"
                          >
                            {playingId === item.id ? <Pause size={14} /> : <Play size={14} />}
                          </button>
                          {playingId === item.id && (
                            <audio
                              src={item.url}
                              autoPlay
                              controls
                              className="w-full"
                              style={{ height: 24 }}
                              onEnded={() => setPlayingId(null)}
                            />
                          )}
                        </div>
                      ) : item.previewUrl ? (
                        <img
                          src={item.previewUrl}
                          alt={item.name}
                          className="h-full w-full object-contain"
                          loading="lazy"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center">
                          <Sparkles size={24} className="text-muted-foreground/40" />
                        </div>
                      )}
                    </div>
                    <div className="p-2">
                      <p className="truncate text-xs font-medium text-foreground">{item.name}</p>
                      <p className="mt-0.5 truncate text-[10px] text-muted-foreground">{formatSize(item.size)}</p>
                    </div>
                  </div>
            ))}
          </div>
        </div>
      )}

      {/* 分页导航 */}
      {!loading && total > 0 && totalPages > 1 && (
        <div className="flex items-center justify-center gap-1.5 pb-3 pt-3">
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            className="flex h-7 items-center gap-0.5 rounded-md px-2 text-xs text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronLeft size={14} />
            上一页
          </button>
          {buildPageList(page + 1, totalPages).map((p, idx) =>
            p === '...' ? (
              <span key={`gap-${idx}`} className="px-1 text-xs text-muted-foreground">…</span>
            ) : (
              <button
                key={p}
                onClick={() => setPage(p - 1)}
                className={cn(
                  'h-7 min-w-7 rounded-md px-2 text-xs transition-colors',
                  page + 1 === p
                    ? 'bg-primary/15 font-medium text-primary'
                    : 'text-muted-foreground hover:bg-muted/40 hover:text-foreground',
                )}
              >
                {p}
              </button>
            ),
          )}
          <button
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            className="flex h-7 items-center gap-0.5 rounded-md px-2 text-xs text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
          >
            下一页
            <ChevronRight size={14} />
          </button>
          <span className="ml-2 text-[11px] text-muted-foreground">共 {total} 项</span>
        </div>
      )}

      {/* 素材预览弹窗 */}
      {previewItem && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setPreviewItem(null)}
        >
          <div
            className="glass-card relative flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* 头部 */}
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="truncate text-sm font-medium text-foreground">{previewItem.name}</p>
                  {(() => {
                    const badge = sourceBadge(previewItem.source);
                    return badge ? (
                      <span className={cn('shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium', badge.className)}>
                        {badge.label}
                      </span>
                    ) : null;
                  })()}
                </div>
                <p className="mt-0.5 text-[11px] text-muted-foreground">
                  {previewItem.subcategory} · {formatSize(previewItem.size)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => downloadToLocal(previewItem.url, previewItem.name)}
                  className="glow-btn flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs"
                >
                  <Download size={13} />
                  下载
                </button>
                <button
                  onClick={() => setPreviewItem(null)}
                  className="rounded-md p-1.5 text-muted-foreground hover:bg-muted/40 hover:text-foreground"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
            {/* 内容 */}
            <div className="flex flex-1 items-center justify-center overflow-auto bg-muted/20 p-6">
              {previewItem.type === 'audio' ? (
                <div className="flex w-full flex-col items-center gap-4">
                  <Music size={48} className="text-muted-foreground/50" />
                  <audio src={previewItem.url} controls autoPlay className="w-full max-w-md" />
                </div>
              ) : (
                <img
                  src={previewItem.previewUrl || previewItem.url}
                  alt={previewItem.name}
                  className="max-h-[60vh] max-w-full object-contain"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
